import { joinPaths } from './path';
import { CSRFError, InternalServerError, RequestError, HttpError } from './errors';

const REFRESH_ATTEMPTS_KEY = 'refresh_attempts';

const MAX_REFRESH_ATTEMPTS = 3;

const ACCESS_TOKEN_KEY = 'session:accesstoken';

const REFRESH_TOKEN_KEY = 'session:refreshtoken';

/* eslint-disable @typescript-eslint/no-explicit-any */

export type ServerResponse =
  | { data?: any; errors: { message: string; extensions?: Record<string, string> }[] }
  | undefined;

export interface AccessToken {
  type: string;
  token: string;
  expiresIn: number;
  now: number;
}

export interface RefreshToken {
  token: string;
  expiresIn: number;
  now: number;
}

export interface Storage {
  getItem: (name: string) => Promise<any | undefined>;
  setItem: (name: string, value: any) => Promise<void>;
  removeItem: (name: string) => Promise<void>;
}

export type ChangeListener = (
  ac: {
    access: AccessToken;
    refresh: RefreshToken;
  } | null,
) => unknown;

export type Listener = () => unknown;

export class TokenSessionManager {
  private csrfTries = 0;
  private onChangeCallbacks: ChangeListener[] = [];
  private onBeforeLogoutCallback: Listener[] = [];
  private fetchFunction: WindowOrWorkerGlobalScope['fetch'];

  constructor(
    private uri: string,
    fetchFunction?: WindowOrWorkerGlobalScope['fetch'],
  ) {
    this.fetchFunction = fetchFunction || window.fetch.bind(window);
  }

  public listenForChanges() {
    window.addEventListener('storage', (e) => {
      if (e.key === null) {
        console.log('[STORAGE] Cleared');
        return;
      }

      if ([ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY].indexOf(e.key) < 0) {
        return;
      }

      console.log('[STORAGE] token updated', {
        oldValue: !!e.oldValue,
        newValue: !!e.newValue,
      });
    });
  }

  async logout() {
    this.runBeforeLogout();

    try {
      await this.fetch(this.prepareURL(), {
        method: 'POST',
        body: JSON.stringify({
          query: 'mutation logout { logout }',
          operationName: 'logout',
        }),
      });
    } finally {
      this.runOnChange();
    }

    return;
  }

  public onChange(listener: ChangeListener): () => void {
    this.onChangeCallbacks.push(listener);

    return () => {
      this.onChangeCallbacks = this.onChangeCallbacks.filter((cb) => cb !== listener);
    };
  }

  public onBeforeLogout(listener: Listener): () => void {
    this.onBeforeLogoutCallback.push(listener);

    return () => {
      this.onBeforeLogoutCallback = this.onBeforeLogoutCallback.filter((cb) => cb !== listener);
    };
  }

  private runOnChange() {
    this.onChangeCallbacks.forEach((cb) => {
      cb(null);
    });
  }

  private runBeforeLogout() {
    this.onBeforeLogoutCallback.forEach((cb) => cb());
  }

  forgotPassword(email: string, token?: string | null): Promise<ServerResponse> {
    return this.serverFetch(this.prepareURL(), {
      method: 'POST',
      body: JSON.stringify({ email, captcha_token: token }),
    });
  }

  resetPassword(email: string, token: string, password: string): Promise<ServerResponse> {
    return this.serverFetch(this.prepareURL(), {
      method: 'POST',
      body: JSON.stringify({ email, token, password }),
    });
  }

  async signIn(values: { email: string; password: string }): Promise<ServerResponse> {
    const response = await this.serverFetch(this.prepareURL(), {
      method: 'POST',
      body: JSON.stringify({
        query: `mutation login($input: LoginInput!) {
          login(input: $input) {
            user {
              ...UserProfile
            }
          }
        } ${this.userFragment()}`,
        operationName: 'login',
        variables: { input: values },
      }),
    });

    return response;
  }

  async serverFetch(input: RequestInfo, init?: RequestInit): Promise<ServerResponse> {
    const response = await this.fetch(input, init);

    return this.handleResponse(response);
  }

  async validateAccount(code: string): Promise<void> {
    const response = await this.serverFetch(this.prepareURL(), {
      method: 'POST',
      body: JSON.stringify({
        query: `mutation validateUserAccount($input: ValidateUserAccountInput!) {
          validateUserAccount(input: $input) {
            user { id validated }
          }
        }`,
        operationName: 'validateUserAccount',
        variables: { input: { code } },
      }),
    });

    if (!response) {
      throw new Error('Empty respose from the server');
    }

    const { data, errors } = response;

    if (errors) {
      throw new RequestError('Invalid response from the server', errors as any);
    }

    if (!data?.validateUserAccount) {
      throw new Error('Got an invalid response form the server: ' + JSON.stringify(data));
    }
  }

  async sendValidationCode(): Promise<void> {
    const response = await this.serverFetch(this.prepareURL(), {
      method: 'POST',
      body: JSON.stringify({
        query: `mutation sendValidationCodeToUserAccount {
          sendValidationCodeToUserAccount {
            user {
              id
              validated
            }
          }
        }`,
        operationName: 'sendValidationCodeToUserAccount',
        variables: {},
      }),
    });

    if (!response) {
      throw new Error('Empty respose from the server');
    }

    const { data, errors } = response;

    if (errors) {
      throw new RequestError('Invalid response from the server', errors as any);
    }

    if (!data?.sendValidationCodeToUserAccount) {
      throw new Error('Got an invalid response form the server: ' + JSON.stringify(data));
    }
  }

  /**
   * Add a middleware to check the CSRF Token.
   */
  async fetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
    const headers = new Headers(init?.headers);
    headers.set('content-type', 'application/json');
    headers.set('accept', 'application/json');

    await this.attachXSRFToken(input, headers);

    const response = await this.fetchFunction(input, {
      ...init,
      headers,
      credentials: 'include',
    });

    // Validar si el backend indica que se debe refrescar el cliente
    const refreshClient = response.headers.get('X-Refresh-Client');
    if (refreshClient !== '1') {
      localStorage.removeItem(REFRESH_ATTEMPTS_KEY);
    } else {
      const storedAttempts = localStorage.getItem(REFRESH_ATTEMPTS_KEY);
      const currentAttempts = storedAttempts ? parseInt(storedAttempts, 10) || 0 : 0;

      if (currentAttempts < MAX_REFRESH_ATTEMPTS) {
        localStorage.setItem(REFRESH_ATTEMPTS_KEY, `${currentAttempts + 1}`);
        window.location.reload();
      }
    }

    // The CSRF token is not longer valid.
    if (response.status === 419) {
      if (this.csrfTries >= 5) {
        throw new Error('Too many tries to fetch the CSRF Token');
      }

      this.csrfTries++;
      await this.fetchXSRFToken();

      const retryResponse = await this.fetch(input, init);
      this.csrfTries = 0;
      return retryResponse;
    }

    return response;
  }

  private prepareURL(): string {
    return joinPaths(this.uri);
  }

  private async handleResponse(response: Response): Promise<ServerResponse> {
    if (response.status === 204) {
      return;
    }

    if (response.ok) {
      return response.json();
    }

    // Error in the input, or bad credentials.
    if (response.status === 422 || response.status === 429) {
      const content = await response.json();

      return content;
    }

    if (response.status === 419) {
      throw CSRFError;
    }

    if (response.status >= 500) {
      throw new InternalServerError(response);
    }

    const content = await response.text();
    throw new HttpError(content || 'Empty response from server', response);
    // TODO If was a internal server error show a message.
    // TODO If there were to many tries show a message.
  }

  private userFragment() {
    return `fragment UserProfile on User {
      id
      name
      lastName
      email
      emailValidated
      profileUpdated
    }`;
  }

  private async attachXSRFToken(input: RequestInfo, headers: Headers) {
    let csrfToken = getCookie('XSRF-TOKEN');
    if (!csrfToken && (typeof input !== 'string' || !input.includes('/sanctum/csrf-cookie'))) {
      await this.fetchXSRFToken();
      csrfToken = getCookie('XSRF-TOKEN');
    }
    headers.set('X-XSRF-TOKEN', csrfToken);
  }

  private async fetchXSRFToken() {
    const parts = this.prepareURL().split('/');
    parts.pop();
    await this.fetch(parts.join('/') + '/sanctum/csrf-cookie');
  }
}

function getCookie(name: string): string {
  const cookiestring = RegExp(name + '=[^;]+').exec(document.cookie);
  return decodeURIComponent(cookiestring ? cookiestring.toString().replace(/^[^=]+./, '') : '');
}

/* eslint-enable @typescript-eslint/no-explicit-any */
